import { useUIStore } from '@/store/uiStore';
import { usePlayerStore } from '@/store/playerStore';
import { useFavoritesStore } from '@/store/userStore';
import { audioEngine } from '@/features/audio/audioEngine';
import { EqualizerBars } from './EqualizerBars';
import { IconButton } from '@/components/ui/IconButton';
import {
  X,
  Play,
  Pause,
  Heart,
  Volume2,
  VolumeX,
  ExternalLink,
  MapPin,
  Radio,
  AlertTriangle,
  Loader2,
  RotateCcw,
} from 'lucide-react';
import { clsx } from 'clsx';

export function ExpandedPlayer() {
  const expanded = useUIStore((s) => s.playerExpanded);
  const setExpanded = useUIStore((s) => s.setPlayerExpanded);
  const station = usePlayerStore((s) => s.currentStation);
  const status = usePlayerStore((s) => s.status);
  const isMuted = usePlayerStore((s) => s.isMuted);
  const volume = usePlayerStore((s) => s.volume);
  const error = usePlayerStore((s) => s.errorMessage);
  const isFav = useFavoritesStore((s) => (station ? s.isFavorite(station.id) : false));
  const toggleFav = useFavoritesStore((s) => s.toggle);

  if (!expanded || !station) return null;

  const isPlaying = status === 'playing';
  const isLoading = status === 'loading';
  const isBuffering = status === 'buffering';
  const isError = status === 'error';
  const showSpinner = isLoading || isBuffering;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-navy-950/95 backdrop-blur-xl"
      role="dialog"
      aria-label="Şimdi çalıyor"
    >
      {/* Drag handle / close */}
      <div className="flex items-center justify-between px-4 pt-4">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Şimdi çalıyor
        </span>
        <IconButton label="Kapat" size="sm" onClick={() => setExpanded(false)}>
          <X className="h-5 w-5" />
        </IconButton>
      </div>

      {/* Artwork */}
      <div className="flex flex-1 flex-col items-center justify-center px-6">
        <div
          className={clsx(
            'relative mb-8 flex h-48 w-48 items-center justify-center rounded-3xl bg-gradient-to-br from-accent-700/40 to-navy-800 shadow-glow',
            isPlaying && 'ring-2 ring-accent-500/30',
          )}
        >
          {showSpinner ? (
            <Loader2 className="h-12 w-12 animate-spin text-accent-300" />
          ) : isError ? (
            <AlertTriangle className="h-12 w-12 text-orange-400" />
          ) : (
            <Radio
              className={clsx(
                'h-16 w-16 text-accent-300 transition-transform',
                isPlaying && 'scale-110',
              )}
            />
          )}
          {isPlaying && (
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">
              <EqualizerBars active size="lg" />
            </div>
          )}
        </div>

        {/* Station name + location */}
        <h2 className="mb-1 text-center text-xl font-bold text-slate-50">
          {station.name}
        </h2>
        <p className="mb-1 flex items-center gap-1 text-sm text-slate-400">
          <MapPin className="h-3.5 w-3.5" />
          {station.cityName ? `${station.cityName}, ` : ''}
          {station.countryName ?? 'Bilinmeyen'}
        </p>
        {station.streams[0] && (
          <p className="mb-6 font-mono text-xs text-slate-500">
            {station.streams[0].codec} · {station.streams[0].bitrate}k ·{' '}
            {station.streams[0].isHls
              ? 'HLS'
              : station.streams[0].isHttps
                ? 'HTTPS'
                : 'HTTP'}
          </p>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-lg bg-orange-500/10 px-4 py-2 text-sm text-orange-400">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}
      </div>

      {/* Tags */}
      {station.tags.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2 px-6 pb-4">
          {station.tags.slice(0, 5).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-accent-500/15 px-3 py-1 text-xs text-accent-200"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="border-t border-white/10 px-6 pb-8 pt-4">
        {/* Primary controls */}
        <div className="mb-6 flex items-center justify-center gap-4">
          {isError ? (
            <button
              type="button"
              onClick={() => audioEngine.retry()}
              className="flex items-center gap-2 rounded-xl bg-accent-500 px-6 py-3 text-sm font-semibold text-navy-950 hover:bg-accent-400"
            >
              <RotateCcw className="h-5 w-5" /> Tekrar dene
            </button>
          ) : (
            <button
              type="button"
              onClick={() => (isPlaying ? audioEngine.pause() : audioEngine.play())}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-navy-950 shadow-glow transition-transform hover:scale-105 active:scale-95"
            >
              {isPlaying ? (
                <Pause className="h-7 w-7" />
              ) : (
                <Play className="ml-1 h-7 w-7" />
              )}
            </button>
          )}
        </div>

        {/* Secondary controls */}
        <div className="flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={() => toggleFav(station.id)}
            aria-label={isFav ? 'Favorilerden çıkar' : 'Favorilere ekle'}
            className="text-slate-400 transition-colors hover:text-slate-200"
          >
            <Heart
              className={clsx('h-6 w-6', isFav && 'fill-magenta-400 text-magenta-400')}
            />
          </button>

          {station.homepageUrl && (
            <a
              href={station.homepageUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Web sitesini aç"
              className="text-slate-400 transition-colors hover:text-slate-200"
            >
              <ExternalLink className="h-6 w-6" />
            </a>
          )}

          <button
            type="button"
            onClick={() => audioEngine.toggleMute()}
            aria-label={isMuted ? 'Sesi aç' : 'Sesi kapat'}
            className="text-slate-400 transition-colors hover:text-slate-200"
          >
            {isMuted || volume === 0 ? (
              <VolumeX className="h-6 w-6" />
            ) : (
              <Volume2 className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Volume slider */}
        <div className="mt-4 flex items-center gap-3">
          <VolumeX className="h-4 w-4 shrink-0 text-slate-500" />
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={isMuted ? 0 : volume}
            onChange={(e) => audioEngine.setVolume(Number(e.target.value))}
            aria-label="Ses seviyesi"
            className="h-1 flex-1 accent-accent-500"
          />
          <Volume2 className="h-4 w-4 shrink-0 text-slate-500" />
        </div>
      </div>
    </div>
  );
}
